import { api, apiUpload } from '@techsofit/app-kit';
import type {
  ChangeGymPlanRequest,
  CreateSubscriptionPlanRequest,
  DemoRequestRow,
  UpdateDemoRequestRequest,
  GymAdminSummary,
  GymDetailView,
  GymStaffView,
  GymMemberView,
  GymPayment,
  GymPaymentRow,
  NotificationChannelsSettings,
  NotificationLogRow,
  NotificationStats,
  PlanFeatureOption,
  PlatformPayment,
  PlatformSettings,
  RecordGymPaymentRequest,
  RevenueSummary,
  SubscriptionPlan,
  UpdateNotificationChannelsRequest,
  UpdatePlatformSettingsRequest,
  UpdateSubscriptionPlanRequest,
  WeeklyReportSendResult,
  MemberAttendanceEmailSendResult,
} from '@techsofit/api-types';

/** Typed calls against the platform API (/api/v1/platform/**). Cookie-auth, admin-only. */

export const getSubscriptionPlans = () => api<SubscriptionPlan[]>('/platform/subscription-plans');

/** Catalog of grantable plan capabilities — renders the entitlement toggles. */
export const getPlanFeatures = () => api<PlanFeatureOption[]>('/platform/subscription-plans/features');

export const createSubscriptionPlan = (req: CreateSubscriptionPlanRequest) =>
  api<SubscriptionPlan>('/platform/subscription-plans', { method: 'POST', body: req });

export const updateSubscriptionPlan = (id: string, req: UpdateSubscriptionPlanRequest) =>
  api<SubscriptionPlan>(`/platform/subscription-plans/${id}`, { method: 'PATCH', body: req });

export const deleteSubscriptionPlan = (id: string) =>
  api<void>(`/platform/subscription-plans/${id}`, { method: 'DELETE' });

export const getPlatformSettings = () => api<PlatformSettings>('/platform/settings');

export const updatePlatformSettings = (req: UpdatePlatformSettingsRequest) =>
  api<PlatformSettings>('/platform/settings', { method: 'PATCH', body: req });

export const getNotificationChannels = () =>
  api<NotificationChannelsSettings>('/platform/notification-channels');

export const updateNotificationChannels = (req: UpdateNotificationChannelsRequest) =>
  api<NotificationChannelsSettings>('/platform/notification-channels', { method: 'PATCH', body: req });

export const sendWeeklyReportsNow = () =>
  api<WeeklyReportSendResult>('/platform/weekly-reports/send-now', { method: 'POST' });

/** Previous calendar month's owner report (same recipients/rules as the weekly one). */
export const sendMonthlyReportsNow = () =>
  api<WeeklyReportSendResult>('/platform/weekly-reports/send-monthly-now', { method: 'POST' });

export const sendMemberAttendanceEmailsNow = () =>
  api<MemberAttendanceEmailSendResult>('/platform/member-attendance-emails/send-now', { method: 'POST' });

/** Recent notifications sent across all gyms (or one gym), optionally within a Harare-time date range. */
export const getPlatformNotifications = (
  opts: { limit?: number; gymId?: string; from?: string; to?: string } = {},
) => {
  const p = new URLSearchParams();
  p.set('limit', String(opts.limit ?? 200));
  if (opts.gymId) p.set('gymId', opts.gymId);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  return api<NotificationLogRow[]>(`/platform/notifications?${p.toString()}`);
};

/** Send-count stats over the last {@code days} — SMS count drives our cost estimate. */
export const getNotificationStats = (days = 30) =>
  api<NotificationStats>(`/platform/notifications/stats?days=${days}`);

export const getGyms = () => api<GymAdminSummary[]>('/platform/gyms');

// ── Demo / sales leads (from the marketing "Book a demo" form) ───────────────
export const getDemoRequests = () => api<DemoRequestRow[]>('/platform/demo-requests');
export const updateDemoRequest = (id: string, body: UpdateDemoRequestRequest) =>
  api<DemoRequestRow>(`/platform/demo-requests/${id}`, { method: 'PATCH', body });
export const deleteDemoRequest = (id: string) =>
  api<void>(`/platform/demo-requests/${id}`, { method: 'DELETE' });

/** Full profile of one gym (overview tab). */
export const getGymDetail = (id: string) => api<GymDetailView>(`/platform/gyms/${id}`);

/** Permanently delete a gym and everything associated with it. [confirm] must
 * equal the gym's exact name. Irreversible. */
export const deleteGym = (id: string, confirm: string) =>
  api<void>(`/platform/gyms/${id}?confirm=${encodeURIComponent(confirm)}`, { method: 'DELETE' });

export const getGymStaff = (id: string) => api<GymStaffView[]>(`/platform/gyms/${id}/staff`);

export const getGymMembers = (id: string) => api<GymMemberView[]>(`/platform/gyms/${id}/members`);

/** Platform revenue at a glance (MRR, collected, status counts). */
export const getRevenue = () => api<RevenueSummary>('/platform/gyms/revenue');

export const getPlatformPayments = (opts: { from?: string; to?: string } = {}) => {
  const p = new URLSearchParams();
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  const qs = p.toString();
  return api<PlatformPayment[]>(`/platform/gyms/payments${qs ? `?${qs}` : ''}`);
};

/** Record a payment a gym made for its subscription — activates / extends / upgrades. */
export const recordGymPayment = (id: string, req: RecordGymPaymentRequest) =>
  api<GymAdminSummary>(`/platform/gyms/${id}/payments`, { method: 'POST', body: req });

export const getGymPayments = (id: string) =>
  api<GymPayment[]>(`/platform/gyms/${id}/payments`);

/** Member payments this gym recorded (membership + packages). */
export const getGymMemberPayments = (id: string) =>
  api<GymPaymentRow[]>(`/platform/gyms/${id}/member-payments`);

export const openSupportView = (id: string) =>
  api<{ url: string; ticket: string }>(`/platform/gyms/${id}/impersonate`, { method: 'POST' });

/** Move a gym to a different tier without a payment (e.g. downgrade to Free). */
export const changeGymPlan = (id: string, req: ChangeGymPlanRequest) =>
  api<GymAdminSummary>(`/platform/gyms/${id}/plan`, { method: 'PATCH', body: req });

export const cancelGymSubscription = (id: string) =>
  api<GymAdminSummary>(`/platform/gyms/${id}/cancel`, { method: 'POST' });

export const reactivateGymSubscription = (id: string) =>
  api<GymAdminSummary>(`/platform/gyms/${id}/reactivate`, { method: 'POST' });

/** Grant/extend a free (comp) trial of {@code days} days — full features, no payment. */
export const grantGymTrial = (id: string, days: number) =>
  api<GymAdminSummary>(`/platform/gyms/${id}/trial`, { method: 'POST', body: { days } });

// ── Fitness content (programs, workouts, badges) ────────────────────────────

export type ProgramType = 'PROGRAM' | 'CHALLENGE';
export type ProgramAudience = 'EVERYONE' | 'WOMEN' | 'MEN';
export type BadgeTier = 'BRONZE' | 'SILVER' | 'GOLD';
export type BadgeCriteria = 'WORKOUTS_COMPLETED' | 'STREAK_DAYS' | 'PROGRAM_COMPLETED';

export interface AdminProgram {
  id: string;
  type: ProgramType;
  title: string;
  subtitle: string | null;
  description: string | null;
  audience: ProgramAudience;
  level: string | null;
  goal: string | null;
  targetAreas: string[];
  equipment: string[];
  durationWeeks: number;
  daysPerWeek: number | null;
  coverImage: string | null;
  accentColor: string | null;
  published: boolean;
  featured: boolean;
  sortOrder: number;
  workoutCount: number;
}

export interface WorkoutItemInput {
  exerciseId: string;
  sets?: number | null;
  reps?: string | null;
  load?: string | null;
  rest?: string | null;
  notes?: string | null;
}

export interface AdminWorkoutItem extends WorkoutItemInput {
  exerciseName: string;
  image: string | null;
}

export interface AdminWorkout {
  id: string;
  programId: string;
  weekNo: number;
  dayNo: number;
  name: string;
  notes: string | null;
  estMinutes: number | null;
  targetAreas: string[];
  sortOrder: number;
  items: AdminWorkoutItem[];
}

export interface AdminBadge {
  id: string;
  code: string;
  title: string;
  description: string | null;
  tier: BadgeTier;
  criteria: BadgeCriteria;
  threshold: number;
  icon: string | null;
  published: boolean;
  sortOrder: number;
}

export interface AdminExercise {
  id: string;
  name: string;
  category: string | null;
  equipment: string | null;
  level: string | null;
  muscleGroups: string[];
  primaryMuscles: string[];
  instructions: string[];
  image: string | null;
  custom: boolean;
}

export interface ExerciseFilters {
  muscleGroups: string[];
  equipment: string[];
  categories: string[];
}

export interface ExerciseQuery {
  q?: string;
  muscleGroup?: string;
  equipment?: string;
  category?: string;
}

export const getPrograms = () => api<AdminProgram[]>('/platform/programs');
export const createProgram = (body: Record<string, unknown>) =>
  api<AdminProgram>('/platform/programs', { method: 'POST', body });
export const updateProgram = (id: string, body: Record<string, unknown>) =>
  api<AdminProgram>(`/platform/programs/${id}`, { method: 'PATCH', body });
export const deleteProgram = (id: string) => api<void>(`/platform/programs/${id}`, { method: 'DELETE' });

export const getWorkouts = (programId: string) =>
  api<AdminWorkout[]>(`/platform/programs/${programId}/workouts`);
export const createWorkout = (programId: string, body: Record<string, unknown>) =>
  api<AdminWorkout>(`/platform/programs/${programId}/workouts`, { method: 'POST', body });
export const updateWorkout = (id: string, body: Record<string, unknown>) =>
  api<AdminWorkout>(`/platform/workouts/${id}`, { method: 'PATCH', body });
export const deleteWorkout = (id: string) => api<void>(`/platform/workouts/${id}`, { method: 'DELETE' });

export const getBadges = () => api<AdminBadge[]>('/platform/badges');
export const createBadge = (body: Record<string, unknown>) =>
  api<AdminBadge>('/platform/badges', { method: 'POST', body });
export const updateBadge = (id: string, body: Record<string, unknown>) =>
  api<AdminBadge>(`/platform/badges/${id}`, { method: 'PATCH', body });
export const deleteBadge = (id: string) => api<void>(`/platform/badges/${id}`, { method: 'DELETE' });

export const searchAdminExercises = (query: ExerciseQuery | string) => {
  const p = typeof query === 'string' ? { q: query } : query;
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.muscleGroup) qs.set('muscleGroup', p.muscleGroup);
  if (p.equipment) qs.set('equipment', p.equipment);
  if (p.category) qs.set('category', p.category);
  const s = qs.toString();
  return api<AdminExercise[]>(`/platform/exercises${s ? `?${s}` : ''}`);
};

export const getExerciseFilters = () => api<ExerciseFilters>('/platform/exercises/filters');

export const createExercise = (body: Record<string, unknown>) =>
  api<AdminExercise>('/platform/exercises', { method: 'POST', body });

export const updateExercise = (id: string, body: Record<string, unknown>) =>
  api<AdminExercise>(`/platform/exercises/${id}`, { method: 'PATCH', body });

export const deleteExercise = (id: string) =>
  api<void>(`/platform/exercises/${id}`, { method: 'DELETE' });

// ── Learn (articles) ────────────────────────────────────────────────────────

export interface AdminArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  category: string | null;
  readMinutes: number | null;
  coverImage: string | null;
  published: boolean;
  featured: boolean;
  sortOrder: number;
}

/** Upload a (cropped) cover image to Azure; returns the stored URL. */
export const uploadCoverImage = (file: Blob, filename = 'cover.jpg') => {
  const form = new FormData();
  form.append('file', file, filename);
  return apiUpload<{ url: string }>('/platform/uploads/image', form);
};

export const getArticles = () => api<AdminArticle[]>('/platform/articles');
export const createArticle = (body: Record<string, unknown>) =>
  api<AdminArticle>('/platform/articles', { method: 'POST', body });
export const updateArticle = (id: string, body: Record<string, unknown>) =>
  api<AdminArticle>(`/platform/articles/${id}`, { method: 'PATCH', body });
export const deleteArticle = (id: string) => api<void>(`/platform/articles/${id}`, { method: 'DELETE' });

// ── Nutrition (recipes) ─────────────────────────────────────────────────────

export interface AdminRecipe {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  category: string | null;
  minutes: number | null;
  calories: number | null;
  protein: number | null;
  coverImage: string | null;
  published: boolean;
  featured: boolean;
  sortOrder: number;
}

export const getRecipes = () => api<AdminRecipe[]>('/platform/recipes');
export const createRecipe = (body: Record<string, unknown>) =>
  api<AdminRecipe>('/platform/recipes', { method: 'POST', body });
export const updateRecipe = (id: string, body: Record<string, unknown>) =>
  api<AdminRecipe>(`/platform/recipes/${id}`, { method: 'PATCH', body });
export const deleteRecipe = (id: string) => api<void>(`/platform/recipes/${id}`, { method: 'DELETE' });
